import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AssigneePicker from './AssigneePicker';

const CATEGORIES = ['Plumbing', 'Tile', 'HVAC', 'Electrical', 'Painting', 'General'];
const STATUSES = ['Evaluating', 'Approved', 'In Progress', 'Complete'];

export default function JobModal({ job, properties, existingPhotos = [], onSaved, onClose }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);
  const [category, setCategory] = useState('General');
  const [status, setStatus] = useState('Evaluating');
  const [actualCost, setActualCost] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [photosToKeep, setPhotosToKeep] = useState(existingPhotos);

  useEffect(() => {
    if (job) {
      setTitle(job.title || '');
      setDescription(job.description || '');
      setPropertyId(job.property_id || '');
      setAssignedTo(job.assigned_to || null);
      setCategory(job.category || 'General');
      setStatus(job.status || 'Evaluating');
      setActualCost(job.actual_cost || '');
      setNotes(job.notes || '');
    } else if (properties.length > 0) {
      setPropertyId(properties[0].id);
    }
  }, [job, properties]);

  const handleFileChange = (e) => {
    setPendingFiles(prev => [...prev, ...Array.from(e.target.files)]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoId) => {
    setPhotosToKeep(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!propertyId) { setError('Property is required.'); return; }
    setLoading(true);
    setError('');

    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      property_id: propertyId,
      assigned_to: assignedTo || null,
      category,
      status,
      actual_cost: actualCost ? parseFloat(actualCost) : null,
      notes: notes.trim() || null,
      updated_at: new Date().toISOString(),
    };

    let savedJob;
    if (job) {
      const { data, error } = await supabase
        .from('jobs')
        .update(payload)
        .eq('id', job.id)
        .select('*, properties(name)')
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      savedJob = data;
    } else {
      const { data, error } = await supabase
        .from('jobs')
        .insert(payload)
        .select('*, properties(name)')
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      savedJob = data;
    }

    // Delete removed photos
    if (job) {
      const removedPhotos = existingPhotos.filter(
        ep => !photosToKeep.find(k => k.id === ep.id)
      );
      for (const photo of removedPhotos) {
        await supabase.storage.from('job-photos').remove([photo.storage_path]);
        await supabase.from('job_photos').delete().eq('id', photo.id);
      }
    }

    // Upload new photos
    for (const file of pendingFiles) {
      const path = `${savedJob.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('job-photos')
        .upload(path, file);
      if (!uploadError) {
        await supabase.from('job_photos').insert({
          job_id: savedJob.id,
          storage_path: path,
          filename: file.name,
        });
      }
    }

    setLoading(false);
    onSaved(savedJob, !job);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{job ? 'Edit Job' : 'Add Job'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Title *</label>
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Master Bath Tile Redo"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the scope of work..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Property *</label>
              <select value={propertyId} onChange={e => setPropertyId(e.target.value)}>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Actual Cost</label>
              <input
                type="number"
                value={actualCost}
                onChange={e => setActualCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <AssigneePicker propertyId={propertyId} value={assignedTo} onChange={setAssignedTo} />

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details..."
            />
          </div>

          <div className="form-group">
            <label>Photos</label>

            {(photosToKeep.length > 0 || pendingFiles.length > 0) && (
              <div className="photo-thumbnails">
                {photosToKeep.map(photo => (
                  <div key={photo.id} className="photo-thumb">
                    <img src={photo.url} alt={photo.filename || 'Photo'} />
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => removeExistingPhoto(photo.id)}
                    >&times;</button>
                  </div>
                ))}
                {pendingFiles.map((file, i) => (
                  <div key={`pending-${i}`} className="photo-thumb">
                    <img src={URL.createObjectURL(file)} alt={file.name} />
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => removePendingFile(i)}
                    >&times;</button>
                  </div>
                ))}
              </div>
            )}

            <label className="photo-upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <span>+ Add Photos</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : job ? 'Save Changes' : 'Add Job'}
          </button>
        </div>
      </div>
    </div>
  );
}
